import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, ArrowRight, ChevronRight } from 'lucide-react';

// ─── Full service detail data ──────────────────────────────────────────────

const SERVICE_DATA = {
  'Web Development': {
    hook: 'From a blank screen to a fast, SEO-ready website in weeks — not months.',
    colour: '#f97316',
    number: '01',
    process: [
      { step: '01', title: 'Discovery & Brief', body: 'We learn your business, your audience, and what "done" looks like for you. No assumptions.' },
      { step: '02', title: 'Wireframe & UX', body: 'Low-fidelity layouts that map every click before a single line of code is written.' },
      { step: '03', title: 'UI Design', body: 'Pixel-tight designs in your brand palette. You approve before we build.' },
      { step: '04', title: 'Development', body: 'React / Next.js, TypeScript, and proper REST APIs. Accessible and fast by default.' },
      { step: '05', title: 'Launch & Handover', body: 'Live deployment, DNS, analytics wired, and your team trained to update content.' },
    ],
    includes: [
      'React & Next.js', 'TypeScript codebase', 'CMS integration',
      'SEO baseline setup', 'Google Analytics 4', 'Mobile-first responsive',
      'Accessibility (WCAG AA)', '3 months post-launch support',
    ],
  },

  'Smart Architecture': {
    hook: 'Systems designed to handle 10 users or 10,000 without rewriting everything.',
    colour: '#f97316',
    number: '02',
    process: [
      { step: '01', title: 'Audit Existing Stack', body: 'We map what you have — databases, APIs, third-party services — and identify the weak points.' },
      { step: '02', title: 'Architecture Blueprint', body: 'A clear diagram of the proposed system: services, data flows, failure modes.' },
      { step: '03', title: 'Tech Selection', body: 'Right tool for the job. Not hype-driven. Postgres vs Mongo, monolith vs microservices — decided with evidence.' },
      { step: '04', title: 'Build & Refactor', body: 'Incremental migration or greenfield build. No big-bang rewrites that break production.' },
      { step: '05', title: 'Scale Testing', body: 'Load testing under realistic traffic before you go live, not after.' },
    ],
    includes: [
      'Database design & optimisation', 'REST / GraphQL API design',
      'Redis caching strategy', 'Auto-scaling configuration',
      'Load testing report', 'Architecture documentation',
      'Code review & handover', 'On-call support window',
    ],
  },

  'Cloud & DevOps': {
    hook: 'Your app stays up during the sale, the launch, and the one time it goes viral.',
    colour: '#f97316',
    number: '03',
    process: [
      { step: '01', title: 'Infrastructure Assessment', body: 'Review current hosting, costs, and single points of failure.' },
      { step: '02', title: 'Migration Plan', body: 'Zero-surprise roadmap. We tell you exactly what moves when and what the risk is.' },
      { step: '03', title: 'AWS Setup', body: 'EC2, RDS, S3, CloudFront — right-sized for your traffic, not for Amazon\'s margin.' },
      { step: '04', title: 'CI/CD Pipeline', body: 'GitHub Actions or equivalent. Every push tested and deployed automatically.' },
      { step: '05', title: 'Monitor & Optimise', body: 'Uptime alerts, cost dashboards, and monthly tuning to trim waste.' },
    ],
    includes: [
      'AWS infrastructure (primary)', 'Docker containerisation',
      'CI/CD pipeline setup', '99.9% uptime SLA',
      'Zero-downtime deployments', 'Cost optimisation audit',
      'Uptime & error monitoring', 'Runbook documentation',
    ],
  },

  'Mobile First': {
    hook: 'Built for the person on a 4-year-old Android with patchy mobile data.',
    colour: '#f97316',
    number: '04',
    process: [
      { step: '01', title: 'UX Research', body: 'Device audits, network conditions, and user journey mapping for your actual audience.' },
      { step: '02', title: 'Prototype', body: 'Interactive mobile prototype validated with real users before a line of code.' },
      { step: '03', title: 'Cross-platform Build', body: 'React Native or Flutter — one codebase, iOS and Android, without cutting corners.' },
      { step: '04', title: 'QA on Real Devices', body: 'Tested on physical handsets across screen sizes and OS versions, not just simulators.' },
      { step: '05', title: 'Store Deploy', body: 'App Store and Google Play submission, metadata, screenshots, and review cycle handled.' },
    ],
    includes: [
      'React Native / Flutter', 'iOS & Android builds',
      'Offline support', 'Push notifications',
      'App Store submission', 'Performance profiling',
      'OTA update pipeline', '60fps animation targets',
    ],
  },

  'Fortress Security': {
    hook: "Kenya's Data Protection Act is not optional. We make sure you're covered before you're checked.",
    colour: '#f97316',
    number: '05',
    process: [
      { step: '01', title: 'Security Audit', body: 'We scan your stack — web app, APIs, hosting, access controls — for known vulnerabilities.' },
      { step: '02', title: 'Vulnerability Report', body: 'Plain-language risk report ranked by severity. No jargon-filled PDFs nobody reads.' },
      { step: '03', title: 'Hardening', body: 'Patch, configure, and remediate in priority order. SSL, headers, input validation, least-privilege access.' },
      { step: '04', title: 'Monitoring Setup', body: 'Intrusion detection, failed-login alerts, and anomaly tracking that actually pages someone.' },
      { step: '05', title: 'Compliance Sign-off', body: 'DPA-aligned documentation and a compliance checklist your legal team can sign off on.' },
    ],
    includes: [
      'Penetration testing', 'SSL/HTTPS & HTTP headers',
      'DDoS mitigation', 'DPA compliance documentation',
      'Access control review', 'Security monitoring',
      'Staff awareness training', 'Quarterly re-audit option',
    ],
  },

  'Automation & Integrations': {
    hook: 'Connect M-Pesa, your CRM, and your email platform. Stop copying data between tabs.',
    colour: '#f97316',
    number: '06',
    process: [
      { step: '01', title: 'Workflow Mapping', body: 'We sit with your team and trace every manual step — the copy-pastes, the exports, the "quick fixes" that became permanent.' },
      { step: '02', title: 'Integration Design', body: 'API architecture that connects your tools reliably, with proper error handling and retries.' },
      { step: '03', title: 'Build', body: 'M-Pesa STK Push, webhook systems, CRM sync, email automation — built and tested in staging.' },
      { step: '04', title: 'End-to-End Testing', body: 'Every flow tested with live API credentials in a sandbox before touching production.' },
      { step: '05', title: 'Handover & Monitoring', body: 'Your team trained on the new workflow. Dashboards set up so you can see what\'s running.' },
    ],
    includes: [
      'M-Pesa STK Push & callbacks', 'Webhook infrastructure',
      'CRM sync (HubSpot / Zoho)', 'Mailchimp / Brevo automation',
      'Google Analytics 4 events', 'Custom admin dashboards',
      'Error alerting & retry logic', 'API documentation',
    ],
  },

  'Networking & Infrastructure': {
    hook: 'Reliable connectivity starts with a network designed right the first time.',
    colour: '#f97316',
    number: '07',
    process: [
      { step: '01', title: 'Site Survey', body: 'Physical walkthrough of your space — walls, interference sources, cable routes, and equipment room.' },
      { step: '02', title: 'Network Design', body: 'Topology diagram with VLAN segmentation, failover paths, and capacity for 3 years of growth.' },
      { step: '03', title: 'Equipment Specification', body: 'Vendor-agnostic recommendations. We tell you what to buy and why — no kickbacks from distributors.' },
      { step: '04', title: 'Installation', body: 'Structured cabling, switch configuration, access point placement, and firewall rules.' },
      { step: '05', title: 'Configuration & Handover', body: 'Network monitoring set up, documentation handed over, and your IT team trained.' },
    ],
    includes: [
      'LAN / WAN design', 'WiFi 6 deployment',
      'Firewall & VPN setup', 'VLAN segmentation',
      'Network monitoring (SNMP)', 'VoIP integration',
      'Structured cabling supervision', 'Network documentation',
    ],
  },

  'Digital Policy & Compliance': {
    hook: 'Policies that actually match how your business operates — not boilerplate copy-paste.',
    colour: '#f97316',
    number: '08',
    process: [
      { step: '01', title: 'IT Audit', body: 'We review your current policies, systems, and data handling practices against DPA requirements.' },
      { step: '02', title: 'Gap Analysis', body: 'Clear report of what\'s missing, what\'s outdated, and what\'s already compliant.' },
      { step: '03', title: 'Policy Drafting', body: 'ICT policy, acceptable use, data retention, and incident response — written for your specific operations.' },
      { step: '04', title: 'Stakeholder Review', body: 'Facilitated review session with your leadership and legal counsel.' },
      { step: '05', title: 'Implementation Support', body: 'Staff briefings, signed acknowledgements, and a review calendar so policies don\'t go stale.' },
    ],
    includes: [
      'ICT policy framework', 'DPA compliance checklist',
      'Data governance structure', 'Risk assessment report',
      'Acceptable use policy', 'Incident response plan',
      'Staff training sessions', 'Annual review schedule',
    ],
  },

  'Digital Marketing': {
    hook: 'Traffic is vanity. Conversions pay salaries. We build for the latter.',
    colour: '#f97316',
    number: '09',
    process: [
      { step: '01', title: 'Audit & Strategy', body: 'Technical SEO audit, competitor analysis, and a growth strategy built on data — not guesswork.' },
      { step: '02', title: 'SEO Foundation', body: 'On-page optimisation, schema markup, Core Web Vitals — the unglamorous work that compounds.' },
      { step: '03', title: 'Content & Campaigns', body: 'Content calendar, search campaigns, and social media strategy aligned to your sales cycle.' },
      { step: '04', title: 'Analytics & Tracking', body: 'GA4 event setup, conversion tracking, and dashboards you can actually read in a Monday morning meeting.' },
      { step: '05', title: 'Optimise & Report', body: 'Monthly performance report with honest commentary: what worked, what didn\'t, and what we\'re changing.' },
    ],
    includes: [
      'Technical SEO audit', 'Google Analytics 4 setup',
      'Search campaign management', 'Social media strategy',
      'Conversion funnel design', 'Email campaign integration',
      'Monthly performance report', 'Competitor tracking',
    ],
  },

  'IT Support & Maintenance': {
    hook: 'Your computers work. Your printers work. Your team works.',
    colour: '#f97316',
    number: '11',
    process: [
      { step: '01', title: 'IT Audit', body: 'One visit to assess every machine, printer, and piece of software in your office. We document what you have and flag what needs attention.' },
      { step: '02', title: 'Priority List', body: 'Clear report — what is broken, what is outdated, what is a security risk. Ranked so you know what to fix first.' },
      { step: '03', title: 'Fix & Optimise', body: 'We resolve the issues, run updates, replace failing hardware, and clean up software licences you are paying for but not using.' },
      { step: '04', title: 'Policy & Documentation', body: 'Simple IT procedures so your team knows how to handle new devices, offboarding, and password resets without calling us every time.' },
      { step: '05', title: 'Ongoing Support', body: 'Monthly or quarterly check-ins to keep everything running. Remote support for day-to-day issues, on-site when you need us.' },
    ],
    includes: [
      'Hardware assessment & upgrades', 'Software updates & licence management',
      'Printer setup & network printing', 'Antivirus & security patching',
      'Data backup setup & monitoring', 'IT policy documentation',
      'Email & productivity tools config', 'Remote & on-site support',
    ],
  },

  'AV System Consulting': {
    hook: 'Bad AV in a boardroom signals bad attention to detail. We fix that.',
    colour: '#f97316',
    number: '10',
    process: [
      { step: '01', title: 'Site Visit', body: 'We come to your space. Measure dimensions, assess acoustics, and understand how the room is actually used.' },
      { step: '02', title: 'Acoustic Analysis', body: 'Identify reflections, bass buildup, and problem frequencies before specifying any equipment.' },
      { step: '03', title: 'System Design', body: 'Complete AV design: speaker placement, display sizing, signal flow, and cable routing — all drawn up before purchasing.' },
      { step: '04', title: 'Equipment Specification', body: 'Brand-agnostic equipment list with rationale. We don\'t push brands — we push results.' },
      { step: '05', title: 'Installation & Tuning', body: 'Physical installation, DSP configuration, calibration, and user training so your team can run it confidently.' },
    ],
    includes: [
      'Conference room AV', 'PA / sound reinforcement',
      'Acoustic treatment advice', 'Projector & screen setup',
      'Microsoft Teams / Zoom Rooms', 'DSP configuration',
      'Video conferencing integration', 'Staff training & documentation',
    ],
  },
};

// ─── Modal component ───────────────────────────────────────────────────────

const ServiceDetailModal = ({ serviceTitle, onClose }) => {
  const data = SERVICE_DATA[serviceTitle];

  // Lock body scroll while open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Escape key to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!data) return null;

  const handleCTA = () => {
    onClose();
    setTimeout(() => {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  return (
    <motion.div
      className="fixed inset-0 z-[10000] flex items-stretch"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      {/* Backdrop */}
      <motion.div
        className="absolute inset-0 bg-gray-950/90 backdrop-blur-sm"
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      />

      {/* Panel — slides in from right */}
      <motion.div
        className="relative ml-auto w-full max-w-2xl bg-gray-950 border-l border-gray-800 overflow-y-auto flex flex-col"
        style={{ borderTop: '4px solid #f97316' }}
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', stiffness: 320, damping: 36 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 z-10 w-10 h-10 flex items-center justify-center border border-gray-700 hover:border-orange-500 hover:text-orange-400 text-gray-400 transition-colors"
          style={{ borderRadius: '2px' }}
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-10 pt-16 flex flex-col gap-12 flex-1">

          {/* Hero */}
          <div>
            <div className="text-[10px] font-mono text-orange-500/60 tracking-[0.4em] uppercase mb-4">
              {data.number} / Service Detail
            </div>
            <h2 className="text-5xl md:text-6xl font-black text-white uppercase leading-none tracking-tight mb-6">
              {serviceTitle}
            </h2>
            <p className="text-xl text-gray-300 leading-relaxed border-l-4 border-orange-500 pl-5">
              {data.hook}
            </p>
          </div>

          {/* Process */}
          <div>
            <div className="text-xs font-black text-orange-500 uppercase tracking-widest mb-6">
              How we do it — 5 steps
            </div>
            <div className="flex flex-col gap-0">
              {data.process.map((p, i) => (
                <div key={p.step} className="flex gap-5 group">
                  {/* Step number + connector */}
                  <div className="flex flex-col items-center shrink-0">
                    <div
                      className="w-9 h-9 flex items-center justify-center border border-gray-700 group-hover:border-orange-500 group-hover:bg-orange-500 transition-colors duration-200 shrink-0"
                      style={{ borderRadius: '2px' }}
                    >
                      <span className="text-[11px] font-black text-gray-400 group-hover:text-white transition-colors">{p.step}</span>
                    </div>
                    {i < data.process.length - 1 && (
                      <div className="w-px flex-1 bg-gray-800 my-1" />
                    )}
                  </div>
                  {/* Content */}
                  <div className={`pb-7 ${i === data.process.length - 1 ? 'pb-0' : ''}`}>
                    <div className="text-sm font-black text-white uppercase tracking-wide mb-1 group-hover:text-orange-400 transition-colors duration-200 pt-2">
                      {p.title}
                    </div>
                    <p className="text-sm text-gray-500 leading-relaxed">{p.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* What's included */}
          <div>
            <div className="text-xs font-black text-orange-500 uppercase tracking-widest mb-6">
              What's included
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              {data.includes.map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <ChevronRight className="w-3.5 h-3.5 text-orange-500 shrink-0" />
                  <span className="text-sm text-gray-300 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-auto pt-6 border-t border-gray-800">
            <button
              onClick={handleCTA}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white py-5 font-black uppercase tracking-widest text-sm flex items-center justify-center gap-3 transition-colors duration-200"
              style={{ borderRadius: '2px' }}
            >
              Discuss {serviceTitle} with Us
              <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-center text-xs text-gray-600 mt-3 tracking-wider">
              We'll scope it honestly and tell you what it actually takes.
            </p>
          </div>

        </div>
      </motion.div>
    </motion.div>
  );
};

export default ServiceDetailModal;
